import React, { useState } from "react";
import { Autocomplete, Box, IconButton, TextField, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Column,
  CustomTableComponent,
} from "../../../shared/elements/customTable.component";
import { PagerComponent } from "../../../shared/elements/pager.component";
import { CustomButtonComponent } from "../../../shared/elements/cutomBtn.component";
import { CustomInputComponent } from "../../../shared/elements/customInputs.component";
import {
  useDeleteNotificationHook,
  useGetNotificationsHook,
  useSendNotificationHook,
} from "../hooks/notifications.hook";
import { useGetUsersHook } from "../../users/hooks/users.hook";
import { UserRow } from "../../users/dto/index.dto";
import { formatDateTime } from "../../../shared/utils/format.util";

type RecipientMode = "all" | "specific";

const columns = (
  onDelete: (id: number) => void
): Column[] => [
  { key: "title", label: "Titre" },
  {
    key: "content",
    label: "Contenu",
    render: (value) => (
      <Typography
        fontSize="0.8rem"
        sx={{
          maxWidth: 240,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </Typography>
    ),
  },
  {
    key: "recipient",
    label: "Destinataire",
    render: (_, row) => (
      <Box>
        <Typography fontWeight="600" fontSize="0.8rem">
          {row.recipient || "—"}
        </Typography>
        <Typography fontSize="0.7rem" color="gray">
          {row.recipientEmail || "—"}
        </Typography>
      </Box>
    ),
  },
  {
    key: "createdAt",
    label: "Envoyé le",
    render: (value) => formatDateTime(value),
  },
  {
    key: "read",
    label: "Lu",
    render: (value) => (
      <Typography
        fontWeight="600"
        fontSize="0.75rem"
        sx={{
          display: "inline-block",
          padding: "2px 10px",
          borderRadius: "10px",
          color: value ? "#2e7d32" : "#9d5b00",
          backgroundColor: value ? "#e6f4ea" : "#fff3e0",
        }}
      >
        {value ? "Lu" : "Non lu"}
      </Typography>
    ),
  },
  {
    key: "actions",
    label: "",
    render: (_, row) => (
      <IconButton
        size="small"
        onClick={() => onDelete(row.id)}
        sx={{ color: "#c0392b" }}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    ),
  },
];

export const NotificationsComponent: React.FC = () => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [recipientMode, setRecipientMode] = useState<RecipientMode>("all");
  const [selectedUser, setSelectedUser] = useState<UserRow | null>(null);
  const [page, setPage] = useState(1);

  const { data: usersData } = useGetUsersHook(1, 100);
  const sendNotificationMutation = useSendNotificationHook();
  const deleteNotificationMutation = useDeleteNotificationHook();
  const { data, isLoading, error } = useGetNotificationsHook(page);

  const canSend =
    title.trim().length > 0 &&
    content.trim().length > 0 &&
    (recipientMode === "all" || !!selectedUser);

  const handleSend = () => {
    if (!canSend) return;
    sendNotificationMutation.mutate(
      {
        title,
        content,
        ...(recipientMode === "specific" && selectedUser
          ? { userId: selectedUser.id }
          : {}),
      },
      {
        onSuccess: () => {
          setTitle("");
          setContent("");
          setSelectedUser(null);
        },
      }
    );
  };

  const handleDelete = (id: number) => {
    deleteNotificationMutation.mutate(id);
  };

  return (
    <Box>
      {/* Send form */}
      <Box
        sx={{
          backgroundColor: "#FFFDF7",
          borderRadius: "12px",
          p: 2,
          mb: 2,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Typography fontWeight="bold" fontSize="1rem" color="#B4006E">
          Envoyer une notification
        </Typography>

        <CustomInputComponent
          label="Titre"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Titre de la notification"
          sx={{ width: "100%" }}
        />

        <TextField
          label="Contenu"
          multiline
          minRows={3}
          fullWidth
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Contenu de la notification"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              backgroundColor: "#fff",
              fontSize: "13px",
            },
          }}
        />

        {/* Recipient mode */}
        <Box display="flex" gap={1}>
          {(["all", "specific"] as RecipientMode[]).map((mode) => (
            <Box
              key={mode}
              onClick={() => setRecipientMode(mode)}
              sx={{
                px: 2.5,
                py: 0.8,
                borderRadius: "18px",
                cursor: "pointer",
                fontSize: "12px",
                fontWeight: 600,
                border: "2px solid #B4006E",
                color: recipientMode === mode ? "#fff" : "#B4006E",
                background:
                  recipientMode === mode
                    ? "linear-gradient(90deg, #B4006E, #FF008D)"
                    : "transparent",
                transition: "all 0.2s",
              }}
            >
              {mode === "all" ? "Tous les utilisateurs" : "Utilisateur spécifique"}
            </Box>
          ))}
        </Box>

        {recipientMode === "specific" && (
          <Autocomplete
            options={usersData?.data || []}
            getOptionLabel={(u) => `${u.fullName} (${u.email})`}
            value={selectedUser}
            onChange={(_, value) => setSelectedUser(value)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Rechercher un utilisateur"
                placeholder="Nom ou email"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    backgroundColor: "#fff",
                    fontSize: "13px",
                  },
                }}
              />
            )}
          />
        )}

        <Box>
          <CustomButtonComponent
            label={sendNotificationMutation.isPending ? "Envoi..." : "Envoyer"}
            onClick={handleSend}
            disabled={!canSend || sendNotificationMutation.isPending}
            sx={{ width: "180px" }}
          />
        </Box>

        {sendNotificationMutation.isSuccess && (
          <Typography fontSize="0.8rem" color="green">
            {sendNotificationMutation.data.message} (
            {sendNotificationMutation.data.recipientCount} destinataire
            {sendNotificationMutation.data.recipientCount > 1 ? "s" : ""})
          </Typography>
        )}
        {sendNotificationMutation.isError && (
          <Typography fontSize="0.8rem" color="#c0392b">
            Erreur lors de l'envoi de la notification.
          </Typography>
        )}
      </Box>

      {/* History table */}
      {isLoading && (
        <Box p={2} textAlign="center" color="#999" fontSize="0.85rem">
          Chargement...
        </Box>
      )}
      {!isLoading && error && (
        <Box p={2} textAlign="center" color="#c0392b" fontSize="0.85rem">
          Erreur lors du chargement des notifications.
        </Box>
      )}
      {data && (
        <>
          <CustomTableComponent
            columns={columns(handleDelete)}
            rows={data.data}
          />
          <PagerComponent
            page={data.page}
            totalPages={data.totalPages}
            total={data.total}
            onPageChange={setPage}
          />
        </>
      )}
    </Box>
  );
};
